import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Wallet, TrendingUp, Link2, Trash2 } from 'lucide-react';
import CryptoIcon from './CryptoIcon';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

// Supported blockchain types with icon names
const BLOCKCHAIN_TYPES = [
  { value: 'ETH', label: 'Ethereum', iconName: 'eth' },
  { value: 'BTC', label: 'Bitcoin', iconName: 'btc' },
  { value: 'SOL', label: 'Solana', iconName: 'sol' },
  { value: 'MATIC', label: 'Polygon', iconName: 'matic' },
  { value: 'AVAX', label: 'Avalanche', iconName: 'avax' },
  { value: 'ARB', label: 'Arbitrum', iconName: 'arb' },
  { value: 'BNB', label: 'BNB Chain', iconName: 'bnb' },
  { value: 'ADA', label: 'Cardano', iconName: 'ada' },
  { value: 'SUI', label: 'Sui', iconName: 'sui' },
  { value: 'HYPE', label: 'Hyperliquid', iconName: 'hype' },
  { value: 'OTHER', label: 'Other', iconName: null }
];

const CRYPTO_SYMBOLS = [
  { value: 'BTC', label: 'Bitcoin', iconName: 'btc' },
  { value: 'ETH', label: 'Ethereum', iconName: 'eth' },
  { value: 'SOL', label: 'Solana', iconName: 'sol' },
  { value: 'MATIC', label: 'Polygon', iconName: 'matic' },
  { value: 'AVAX', label: 'Avalanche', iconName: 'avax' },
  { value: 'USDC', label: 'USD Coin', iconName: 'usdc' },
  { value: 'USDT', label: 'Tether', iconName: 'usdt' },
  { value: 'ARB', label: 'Arbitrum', iconName: 'arb' },
  { value: 'BNB', label: 'BNB', iconName: 'bnb' },
  { value: 'ADA', label: 'Cardano', iconName: 'ada' },
  { value: 'SUI', label: 'Sui', iconName: 'sui' },
  { value: 'HYPE', label: 'Hyperliquid', iconName: 'hype' },
  { value: 'OTHER', label: 'Other', iconName: null }
];

const INVESTMENT_FOCUS_OPTIONS = [
  'DeFi', 'NFT', 'Gaming', 'Layer 1', 'Layer 2', 'Infrastructure',
  'Metaverse', 'DAO', 'Web3 Social', 'Privacy', 'Staking', 'Lending',
  'DEX', 'Bridges', 'Oracles', 'Storage', 'Identity', 'Other'
];

export const WalletAddressManager = ({ walletAddresses = [], onChange }) => {
  const [newWallet, setNewWallet] = useState({ type: 'ETH', address: '', label: '' });

  const addWallet = () => {
    if (!newWallet.address.trim()) return;
    
    onChange([...walletAddresses, { ...newWallet }]);
    setNewWallet({ type: 'ETH', address: '', label: '' });
  };

  const removeWallet = (index) => {
    onChange(walletAddresses.filter((_, i) => i !== index));
  };

  const updateWallet = (index, field, value) => {
    const updated = [...walletAddresses];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  return (
    <div className="space-y-4">
      <div className="mb-3">
        <Label className="text-sm font-medium">Wallet Addresses</Label>
      </div>

      {/* Existing Wallets - Table */}
      {walletAddresses.length > 0 && (
        <div className="mb-4">
          <div className="border border-white/10 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#1a1a1a]">
                <tr>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-2">Type</th>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-2">Address</th>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-2">Label</th>
                  <th className="text-right text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                <AnimatePresence>
                  {walletAddresses.map((wallet, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="bg-[#0a0a0a] hover:bg-[#1a1a1a] transition-colors"
                    >
                      <td className="px-4 py-3">
                        <Badge variant="secondary" className="text-xs font-mono">
                          {wallet.type}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-xs text-zinc-300 font-mono break-all">
                          {wallet.address}
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-zinc-400">
                          {wallet.label || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeWallet(index)}
                          className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add New Wallet */}
      <Card className="p-3 bg-[#0a0a0a] border-dashed border-2 border-white/10">
        <div className="space-y-2">
          <div className="flex gap-2">
            <Select value={newWallet.type} onValueChange={(value) => setNewWallet({ ...newWallet, type: value })}>
              <SelectTrigger className="w-[157px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BLOCKCHAIN_TYPES.map(chain => (
                  <SelectItem key={chain.value} value={chain.value}>
                    <div className="flex items-center gap-2">
                      {chain.iconName && <CryptoIcon name={chain.iconName} size={14} />}
                      <span>{chain.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Optional label"
              value={newWallet.label}
              onChange={(e) => setNewWallet({ ...newWallet, label: e.target.value })}
              className="h-9"
            />
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Enter wallet address"
              value={newWallet.address}
              onChange={(e) => setNewWallet({ ...newWallet, address: e.target.value })}
              className="font-mono text-sm h-9"
            />
            <Button type="button" onClick={addWallet} size="sm" className="shrink-0">
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export const CryptoHoldingsManager = ({ cryptoHoldings = {}, onChange }) => {
  const [newHolding, setNewHolding] = useState({ symbol: 'BTC', amount: '', costBasis: '', acquisitionDate: '' });
  const [customSymbol, setCustomSymbol] = useState('');

  const addHolding = () => {
    const symbol = newHolding.symbol === 'OTHER' ? customSymbol.toUpperCase() : newHolding.symbol;
    if (!symbol || !newHolding.amount) return;

    onChange({
      ...cryptoHoldings,
      [symbol]: {
        amount: newHolding.amount,
        costBasis: newHolding.costBasis || undefined,
        acquisitionDate: newHolding.acquisitionDate || undefined
      }
    });

    setNewHolding({ symbol: 'BTC', amount: '', costBasis: '', acquisitionDate: '' });
    setCustomSymbol('');
  };

  const removeHolding = (symbol) => {
    const updated = { ...cryptoHoldings };
    delete updated[symbol];
    onChange(updated);
  };

  const updateHolding = (symbol, field, value) => {
    onChange({
      ...cryptoHoldings,
      [symbol]: {
        ...cryptoHoldings[symbol],
        [field]: value
      }
    });
  };

  return (
    <div className="space-y-4">
      <div className="mb-3">
        <Label className="text-sm font-medium">Crypto Holdings</Label>
      </div>

      {/* Existing Holdings - Table */}
      {Object.keys(cryptoHoldings).length > 0 && (
        <div className="mb-4">
          <div className="border border-white/10 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#1a1a1a]">
                <tr>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-2">Symbol</th>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-2">Amount</th>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-2">Cost Basis</th>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-2">Acquired</th>
                  <th className="text-right text-xs font-medium text-zinc-400 uppercase tracking-wider px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                <AnimatePresence>
                  {Object.entries(cryptoHoldings).map(([symbol, holding]) => {
                    // Ensure holding is an object with expected structure
                    if (!holding || typeof holding !== 'object') return null;
                    
                    // Handle both camelCase and snake_case
                    const amount = holding.amount || holding.Amount || '';
                    const costBasis = holding.costBasis || holding.CostBasis || holding.cost_basis || '';
                    const acquisitionDate = holding.acquisitionDate || holding.AcquisitionDate || holding.acquisition_date || '';
                    
                    return (
                      <motion.tr
                        key={symbol}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="bg-[#0a0a0a] hover:bg-[#1a1a1a] transition-colors"
                      >
                        <td className="px-4 py-3">
                          <Badge variant="default" className="font-mono text-xs">
                            {symbol}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-zinc-300 font-medium">
                            {amount || '0'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-zinc-400">
                            {costBasis ? `$${costBasis}` : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-zinc-400">
                            {acquisitionDate ? new Date(acquisitionDate).toLocaleDateString() : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeHolding(symbol)}
                            className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add New Holding */}
      <Card className="p-3 bg-[#0a0a0a] border-dashed border-2 border-white/10">
        <div className="space-y-2">
          <div className="flex gap-2">
            <Select 
              value={newHolding.symbol} 
              onValueChange={(value) => setNewHolding({ ...newHolding, symbol: value })}
            >
              <SelectTrigger className="w-[120px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CRYPTO_SYMBOLS.map(sym => (
                  <SelectItem key={sym.value} value={sym.value}>
                    <div className="flex items-center gap-2">
                      {sym.iconName && <CryptoIcon name={sym.iconName} size={14} />}
                      <span>{sym.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {newHolding.symbol === 'OTHER' && (
              <Input
                placeholder="Enter symbol"
                value={customSymbol}
                onChange={(e) => setCustomSymbol(e.target.value)}
                className="h-9 w-[120px]"
              />
            )}
            <Input
              type="text"
              placeholder="Amount"
              value={newHolding.amount}
              onChange={(e) => setNewHolding({ ...newHolding, amount: e.target.value })}
              className="h-9 flex-1"
            />
          </div>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Cost basis (optional)"
              value={newHolding.costBasis}
              onChange={(e) => setNewHolding({ ...newHolding, costBasis: e.target.value })}
              className="h-9"
            />
            <Input
              type="date"
              value={newHolding.acquisitionDate}
              onChange={(e) => setNewHolding({ ...newHolding, acquisitionDate: e.target.value })}
              className="h-9"
            />
            <Button type="button" onClick={addHolding} size="sm" className="shrink-0">
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export const InvestmentFocusManager = ({ investmentFocus = [], onChange }) => {
  const toggleFocus = (focus) => {
    if (investmentFocus.includes(focus)) {
      onChange(investmentFocus.filter(f => f !== focus));
    } else {
      onChange([...investmentFocus, focus]);
    }
  };

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">Investment Focus Areas</Label>
      <div className="flex flex-wrap gap-2">
        {INVESTMENT_FOCUS_OPTIONS.map(focus => (
          <Badge
            key={focus}
            variant={investmentFocus.includes(focus) ? "default" : "outline"}
            className="cursor-pointer hover:bg-blue-100 transition-colors"
            onClick={() => toggleFocus(focus)}
          >
            {focus}
          </Badge>
        ))}
      </div>
    </div>
  );
};
